import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';
import { classifyApiError, isAuthFailure, isNetworkFailure } from '@/lib/network/apiErrors';

const AuthContext = createContext(null);

function getStoredToken() {
  return sessionStorage.getItem('resqly_admin_token') || localStorage.getItem('resqly_token');
}

function getStoredRole() {
  return sessionStorage.getItem('resqly_admin_role') || localStorage.getItem('resqly_role');
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getStoredToken());
  const [role, setRole] = useState(() => getStoredRole());
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    if (!getStoredToken()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/me', {
        __skipSafeRetry: true,
      });
      setMe(data.user);
      setRole(data.user.role);

      if (data.user.role === 'admin') {
        sessionStorage.setItem('resqly_admin_role', 'admin');
        localStorage.removeItem('resqly_role');
      } else {
        localStorage.setItem('resqly_role', data.user.role);
        sessionStorage.removeItem('resqly_admin_role');
      }
    } catch (e) {
      if (isNetworkFailure(e)) {
        // Retain session during temporary connectivity loss.
        setLoading(false);
        return;
      }
      if (isAuthFailure(e) || e.response?.status === 401) {
        setMe(null);
        setToken(null);
        setRole(null);
        localStorage.removeItem('resqly_token');
        localStorage.removeItem('resqly_role');
        sessionStorage.removeItem('resqly_admin_token');
        sessionStorage.removeItem('resqly_admin_role');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = (tk, r) => {
    if (r === 'admin') {
      sessionStorage.setItem('resqly_admin_token', tk);
      sessionStorage.setItem('resqly_admin_role', r);
      localStorage.removeItem('resqly_token');
      localStorage.removeItem('resqly_role');
    } else {
      localStorage.setItem('resqly_token', tk);
      localStorage.setItem('resqly_role', r);
      sessionStorage.removeItem('resqly_admin_token');
      sessionStorage.removeItem('resqly_admin_role');
    }
    setToken(tk);
    setRole(r);
    loadMe();
  };

  const logout = () => {
    localStorage.removeItem('resqly_token');
    localStorage.removeItem('resqly_role');
    sessionStorage.removeItem('resqly_admin_token');
    sessionStorage.removeItem('resqly_admin_role');
    setToken(null);
    setRole(null);
    setMe(null);
  };

  return (
    <AuthContext.Provider value={{ token, role, me, loading, login, logout, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
